import { supabase } from '../lib/supabase'
import { Notification } from '../types/notifications'

export interface DatabaseNotification {
  id: string
  type: Notification['type']
  title: string
  message: string
  priority: Notification['priority']
  category: Notification['category']
  action_url?: string
  action_text?: string
  expires_at?: string
  created_at: string
  metadata?: any
}

export interface UserNotificationStatus {
  id: string
  notification_id: string
  user_id: string
  is_read: boolean
  read_at?: string
  created_at: string
}

class DatabaseNotificationService {
  
  /**
   * Create a new notification in the database (admin only)
   */
  async createNotification(notification: Omit<DatabaseNotification, 'id' | 'created_at'>): Promise<DatabaseNotification | null> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert([notification])
        .select()
        .single()

      if (error) {
        console.error('Error creating notification:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error creating notification:', error)
      return null
    }
  }

  /**
   * Get all active notifications from database
   */
  async getNotifications(): Promise<DatabaseNotification[]> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .or('expires_at.is.null,expires_at.gt.now()')
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching notifications:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching notifications:', error)
      return []
    }
  }

  /**
   * Get notifications with read status for current authenticated user
   */
  async getNotificationsForUser(): Promise<(DatabaseNotification & { is_read?: boolean; read_at?: string })[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        console.error('No authenticated user')
        return []
      }

      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          user_notifications!left(is_read, read_at)
        `)
        .or('expires_at.is.null,expires_at.gt.now()')
        .eq('user_notifications.user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching user notifications:', error)
        return []
      }

      return data?.map(notification => ({
        ...notification,
        is_read: notification.user_notifications?.[0]?.is_read || false,
        read_at: notification.user_notifications?.[0]?.read_at
      })) || []
    } catch (error) {
      console.error('Error fetching user notifications:', error)
      return []
    }
  }

  /**
   * Mark notification as read for current authenticated user
   */
  async markAsRead(notificationId: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        console.error('No authenticated user')
        return false
      }

      const { error } = await supabase
        .from('user_notifications')
        .upsert({
          notification_id: notificationId,
          user_id: user.id,
          is_read: true,
          read_at: new Date().toISOString()
        })

      if (error) {
        console.error('Error marking notification as read:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error marking notification as read:', error)
      return false
    }
  }

  /**
   * Get unread notification count for current authenticated user
   */
  async getUnreadCount(): Promise<number> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        return 0
      }

      const { data, error } = await supabase
        .from('notifications')
        .select(`
          id,
          user_notifications!left(is_read)
        `)
        .or('expires_at.is.null,expires_at.gt.now()')
        .eq('user_notifications.user_id', user.id)
        .or('user_notifications.is_read.is.null,user_notifications.is_read.eq.false')

      if (error) {
        console.error('Error getting unread count:', error)
        return 0
      }

      return data?.length || 0
    } catch (error) {
      console.error('Error getting unread count:', error)
      return 0
    }
  }

  /**
   * Delete a notification (admin only)
   */
  async deleteNotification(notificationId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)

      if (error) {
        console.error('Error deleting notification:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error deleting notification:', error)
      return false
    }
  }

  /**
   * Clear all notifications (admin only)
   */
  async clearAllNotifications(): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000') // Delete all

      if (error) {
        console.error('Error clearing notifications:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error clearing notifications:', error)
      return false
    }
  }

  /**
   * Get notification statistics
   */
  async getStats(): Promise<{
    total: number
    unread: number
    byType: Record<string, number>
    byCategory: Record<string, number>
    byPriority: Record<string, number>
  }> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      // Get all notifications
      const { data: allNotifications, error: allError } = await supabase
        .from('notifications')
        .select('type, category, priority')
        .or('expires_at.is.null,expires_at.gt.now()')

      if (allError) {
        console.error('Error fetching notification stats:', allError)
        return { total: 0, unread: 0, byType: {}, byCategory: {}, byPriority: {} }
      }

      // Get unread count for current user
      let unreadCount = 0
      if (user) {
        unreadCount = await this.getUnreadCount()
      }

      const total = allNotifications?.length || 0
      
      // Group by type, category, and priority
      const byType: Record<string, number> = {}
      const byCategory: Record<string, number> = {}
      const byPriority: Record<string, number> = {}

      allNotifications?.forEach(notification => {
        byType[notification.type] = (byType[notification.type] || 0) + 1
        byCategory[notification.category] = (byCategory[notification.category] || 0) + 1
        byPriority[notification.priority] = (byPriority[notification.priority] || 0) + 1
      })

      return {
        total,
        unread: unreadCount,
        byType,
        byCategory,
        byPriority
      }
    } catch (error) {
      console.error('Error getting notification stats:', error)
      return { total: 0, unread: 0, byType: {}, byCategory: {}, byPriority: {} }
    }
  }

  /**
   * Check if notification tables exist in the database
   */
  async checkTablesExist(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('id')
        .limit(1)

      // If we can query the table without error, it exists
      return !error
    } catch (error) {
      console.error('Notification tables do not exist:', error)
      return false
    }
  }
}

export const databaseNotificationService = new DatabaseNotificationService() 